import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * Tailwind-styled markdown renderer. The project uses Tailwind v4 without the
 * typography plugin, so element styling is supplied explicitly here.
 */
const components: Components = {
  h1: ({ children }) => (
    <h1 className="mt-2 mb-4 text-3xl font-bold tracking-tight text-slate-900">{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="mt-10 mb-3 border-b border-slate-200 pb-1.5 text-xl font-semibold text-slate-900">
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 className="mt-6 mb-2 text-base font-semibold text-slate-800">{children}</h3>
  ),
  p: ({ children }) => <p className="my-3 leading-7 text-slate-700">{children}</p>,
  a: ({ href, children }) => (
    <a href={href} className="font-medium text-blue-600 underline-offset-2 hover:underline">
      {children}
    </a>
  ),
  ul: ({ children }) => (
    <ul className="my-3 ml-5 list-disc space-y-1.5 text-slate-700">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="my-3 ml-5 list-decimal space-y-1.5 text-slate-700">{children}</ol>
  ),
  li: ({ children }) => <li className="leading-7">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-slate-900">{children}</strong>,
  blockquote: ({ children }) => (
    <blockquote className="my-4 border-l-4 border-slate-300 bg-slate-50 py-1 pl-4 text-slate-600">
      {children}
    </blockquote>
  ),
  hr: () => <hr className="my-8 border-slate-200" />,
  code: ({ className, children }) => {
    const isBlock = className?.includes('language-');
    if (isBlock) {
      return <code className={`${className} font-mono text-sm`}>{children}</code>;
    }
    return (
      <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[13px] text-slate-800">
        {children}
      </code>
    );
  },
  pre: ({ children }) => (
    <pre className="my-4 overflow-x-auto rounded-lg border border-slate-800 bg-slate-900 p-4 text-slate-100">
      {children}
    </pre>
  ),
  table: ({ children }) => (
    <div className="my-4 overflow-x-auto">
      <table className="w-full border-collapse text-left text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-slate-50">{children}</thead>,
  th: ({ children }) => (
    <th className="border border-slate-200 px-3 py-2 font-semibold text-slate-700">{children}</th>
  ),
  td: ({ children }) => (
    <td className="border border-slate-200 px-3 py-2 align-top text-slate-600">{children}</td>
  ),
};

export default function Markdown({ content }: { content: string }) {
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
      {content}
    </ReactMarkdown>
  );
}
